export type AppMode = 'manage' | 'work'

export const MODE_SEARCH_KEY = 'mode'

export const MANAGE_MODE_DEFAULT_PATH = '/recents'
export const WORK_MODE_DEFAULT_PATH = '/dashboard'

export const WORK_MODE_ALLOWED_PATHS = [
  '/dashboard',
  '/task',
  '/account',
  '/task-list',
  '/feature-flags',
] as const

export const inferAppModeFromPath = (pathname: string): AppMode | undefined => {
  if (
    pathname === '/dashboard' ||
    (pathname.startsWith('/task') && !pathname.startsWith('/task-view'))
  ) {
    return 'work'
  }

  if (
    pathname === '/recents' ||
    pathname === '/priority-config' ||
    pathname.includes('/spaces')
  ) {
    return 'manage'
  }

  return undefined
}

// Legacy cleanup: `?mode=` used to live in the URL and got persisted into
// `lastManagePath`/`lastWorkPath`. Keep stripping it on every path write until
// old paths die out, otherwise mode switching would
// resurrect the param from a stale stored path.
export const stripAppModeFromPath = (path: string): string => {
  const [pathname, searchStr] = path.split('?')

  if (!searchStr) {
    return path
  }

  const params = new URLSearchParams(searchStr)

  params.delete(MODE_SEARCH_KEY)

  const remaining = params.toString()

  return remaining ? `${pathname}?${remaining}` : pathname
}

export const parsePathAndSearch = (
  path: string,
): { pathname: string; search: Record<string, string> } => {
  const [pathname, searchStr] = path.split('?')
  const params = new URLSearchParams(searchStr || '')

  // Same legacy cleanup as in stripAppModeFromPath: persisted paths saved
  // before the migration may still carry `?mode=` — never put it back in the URL.
  params.delete(MODE_SEARCH_KEY)

  return {
    pathname,
    search: Object.fromEntries(params),
  }
}

export const resolveAppMode = ({
  pathname,
  isCloudWorker,
  lastActiveMode,
}: {
  pathname: string
  isCloudWorker: boolean
  lastActiveMode: AppMode
}): AppMode => {
  if (isCloudWorker) {
    return 'work'
  }

  return inferAppModeFromPath(pathname) ?? lastActiveMode
}

export const isPathAllowedInWorkMode = (pathname: string): boolean =>
  WORK_MODE_ALLOWED_PATHS.some(
    (allowedPath) =>
      pathname === allowedPath || pathname.startsWith(`${allowedPath}/`),
  )

export const isWorkModeRestrictedPath = ({
  mode,
  pathname,
  isCloudWorker,
}: {
  mode: AppMode
  pathname: string
  isCloudWorker: boolean
}): boolean => {
  const isWorkModeRestricted = isCloudWorker || mode === 'work'

  return isWorkModeRestricted && !isPathAllowedInWorkMode(pathname)
}
